// Break-even model for Skyhawks Peninsula.
//
// Costs are classified by BASIS, not by category, because the basis decides
// which side of the break-even ratio a cost lands on:
//
//   break-even programs = fixed costs per quarter / contribution per program run
//
// A cost that scales with coach wages scales with programs run, so it belongs
// in the denominator. Putting it in the numerator corrupts both sides at once.
//
// See docs/superpowers/specs/2026-08-08-break-even-tracker-design.md

const RAN_STATUSES = ['paid', 'complete', 'invoiced', 'active']
const UNRESOLVED_STATUSES = ['registration', 'at-risk']
const DOW_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

// PayrollService._calculateHoursForSession / _calculateEquipmentAndPrepHours
const CLASS_SETUP_HOURS = 0.25
const CAMP_SETUP_FIRST = 7 / 12
const CAMP_SETUP_REST = 5 / 12

export const programRan = (p) => RAN_STATUSES.includes(p.status)
export const programUnresolved = (p) => UNRESOLVED_STATUSES.includes(p.status)
export const isTestProgram = (p) => p.status === 'test'

/** finalCount, then registrationCount, then roster length. */
export function enrollmentOf(program) {
  for (const key of ['finalCount', 'registrationCount']) {
    const raw = program[key]
    if (raw !== null && raw !== undefined && raw !== '' && raw !== 0) {
      const n = Number(raw)
      if (!Number.isNaN(n)) return n
    }
  }
  return (program.roster || []).length
}

/** Session count: numSessions if present, else date range x daysOfWeek. */
export function sessionCountOf(program) {
  const raw = program.numSessions
  if (raw !== null && raw !== undefined && raw !== '' && raw !== 0) {
    const n = Number(raw)
    if (!Number.isNaN(n) && n > 0) return Math.round(n)
  }
  const start = new Date(`${program.startDate}T00:00:00`)
  const end = new Date(`${program.endDate}T00:00:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 8

  const days = (program.daysOfWeek || [])
    .map((d) => DOW_INDEX[d])
    .filter((d) => d !== undefined)
  // Without daysOfWeek there is no basis for counting individual days;
  // fall back rather than counting every calendar day in the range.
  if (!days.length) return 8

  let count = 0
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    if (days.includes(d.getDay())) count += 1
  }
  return Math.max(count, 1)
}

export function sessionLengthHours(program) {
  const [sh, sm] = String(program.startTime || '').split(':').map(Number)
  const [eh, em] = String(program.endTime || '').split(':').map(Number)
  if ([sh, sm, eh, em].some(Number.isNaN)) return 1
  return Math.max((eh * 60 + em - sh * 60 - sm) / 60, 0.25)
}

/** Uncalibrated coach hours for one program, assuming a single coach. */
export function coachHoursOf(program) {
  const sessions = sessionCountOf(program)
  const length = sessionLengthHours(program)
  const setup = program.type === 'camp'
    ? CAMP_SETUP_FIRST + (sessions - 1) * CAMP_SETUP_REST
    : CLASS_SETUP_HOURS * sessions
  return sessions * length + setup
}

export function revShareOf(program) {
  const gross = enrollmentOf(program) * Number(program.price || 0)
  const split = Number(program.feeSplit || 0)
  return program.feeType === 'percent' ? (gross * split) / 100 : split
}

const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0)

/**
 * Derive baselines from real programs. `seasons` is an ordered array of
 * { key, label, year, season, programs }.
 */
export function deriveBaselines(seasons) {
  const live = seasons.map((s) => ({
    ...s,
    programs: s.programs.filter((p) => !isTestProgram(p)),
  }))
  const all = live.flatMap((s) => s.programs)
  const ran = all.filter(programRan)

  const byType = (type) => {
    const subset = ran.filter((p) => p.type === type)
    if (!subset.length) return null
    const gross = subset.map((p) => enrollmentOf(p) * Number(p.price || 0))
    return {
      count: subset.length,
      enrollment: mean(subset.map(enrollmentOf)),
      price: mean(gross) / (mean(subset.map(enrollmentOf)) || 1),
      grossPerProgram: mean(gross),
      netPerProgram: mean(subset.map((p) => enrollmentOf(p) * Number(p.price || 0) - revShareOf(p))),
      coachHours: mean(subset.map(coachHoursOf)),
    }
  }

  const totalGross = ran.reduce((a, p) => a + enrollmentOf(p) * Number(p.price || 0), 0)
  const totalShare = ran.reduce((a, p) => a + revShareOf(p), 0)

  // Unresolved programs are excluded from both sides of the cancellation rate.
  const resolved = all.filter((p) => programRan(p) || p.status === 'canceled')

  return {
    seasons: live.map((s) => {
      const resolvedHere = s.programs.filter((p) => programRan(p) || p.status === 'canceled')
      return {
        key: s.key,
        label: s.label,
        year: s.year,
        season: s.season,
        summer: s.season === 'summer',
        offered: s.programs.length,
        ran: resolvedHere.length ? s.programs.filter(programRan).length : null,
        pending: s.programs.filter(programUnresolved).length,
      }
    }),
    class: byType('class'),
    camp: byType('camp'),
    revShare: totalGross ? totalShare / totalGross : 0,
    cancelRate: resolved.length
      ? resolved.filter((p) => p.status === 'canceled').length / resolved.length
      : 0,
    programCount: all.length,
    ranCount: ran.length,
  }
}

/** Per-program economics at the current settings. */
export function programEconomics(s) {
  const build = (enrollment, price, hours, shirtPerKid) => {
    const gross = enrollment * price
    return {
      gross,
      net: gross * (1 - s.revShare),
      wages: hours * s.coachRate * s.calibration * (1 + s.coachBurden),
      shirt: shirtPerKid * enrollment,
    }
  }
  return {
    class: build(s.classEnrollment, s.classPrice, s.classCoachHours, 0),
    camp: build(s.campEnrollment, s.campPrice, s.campCoachHours, s.campShirtPerKid),
  }
}

export function fixedCostsPerQuarter(s) {
  const lineTotal = s.costLines
    .filter((c) => c.enabled)
    .reduce((a, c) => a + Number(c.amount || 0), 0)
  const managers = s.managerCount * s.managerHoursPerWeek * s.managerRate * 13
  return lineTotal + managers * (1 + s.managerBurden)
}

/** Revenue-side totals for a quarter running `n` programs. */
function quarterVolume(n, s, econ, summer) {
  const campShare = summer ? s.summerCampMix : 0
  const camps = n * campShare
  const classes = n * (1 - campShare)
  return {
    gross: camps * econ.camp.gross + classes * econ.class.gross,
    net: camps * econ.camp.net + classes * econ.class.net,
    wages: camps * econ.camp.wages + classes * econ.class.wages,
    shirt: camps * econ.camp.shirt,
  }
}

/**
 * Royalty is piecewise: max(rate x revenue, minimum). Below the crossover it
 * behaves as a fixed cost, above it as a variable one. Yearly reconciliation
 * lets summer's excess absorb the school-year shortfall, so it is computed
 * against annual revenue and allocated back pro-rata.
 */
export function royaltyForYear(annualNet, s) {
  return Math.max(s.royaltyRate * annualNet, s.royaltyMinMonthly * 12)
}

function royaltyForQuarter(quarterNet, annualNet, s) {
  if (s.yearlyReconciliation) {
    if (annualNet <= 0) return s.royaltyMinMonthly * 3
    return royaltyForYear(annualNet, s) * (quarterNet / annualNet)
  }
  return Math.max(s.royaltyRate * quarterNet, s.royaltyMinMonthly * 3)
}

/**
 * Profit for one quarter. `annualNet` supplies the context yearly
 * reconciliation needs; pass the projected full-year net revenue.
 */
export function quarterProfit(n, s, econ, summer, annualNet) {
  const v = quarterVolume(n, s, econ, summer)
  const royalty = royaltyForQuarter(v.net, annualNet, s)
  // The FDD requires >= 3% of gross revenue on local marketing; the budget is
  // a floor the requirement can lift, never lower.
  const advertising = Math.max(s.localAdRate * v.gross, s.adBudgetPerQuarter)
  return v.net - v.wages - v.shirt - royalty - advertising - fixedCostsPerQuarter(s)
}

/** Projected annual net revenue at the given run rates. */
export function projectedAnnualNet(s, econ, runsSchoolYear, runsSummer) {
  const sy = quarterVolume(runsSchoolYear, s, econ, false).net
  const su = quarterVolume(runsSummer, s, econ, true).net
  return sy * 3 + su
}

/** Smallest whole program count that clears break-even, or null if unreachable. */
export function breakEvenCount(s, econ, summer, annualNet, limit = 400) {
  for (let n = 1; n <= limit; n += 1) {
    if (quarterProfit(n, s, econ, summer, annualNet) >= 0) return n
  }
  return null
}

export function programsToOffer(runCount, cancelRate) {
  if (runCount === null) return null
  if (cancelRate >= 1) return null
  return Math.ceil(runCount / (1 - cancelRate))
}

/** Everything the page renders, in one pass. */
export function computeModel(s, runsSchoolYear, runsSummer) {
  const econ = programEconomics(s)
  const annualNet = projectedAnnualNet(s, econ, runsSchoolYear, runsSummer)

  const schoolYear = {
    breakEven: breakEvenCount(s, econ, false, annualNet),
    actual: runsSchoolYear,
    profit: quarterProfit(runsSchoolYear, s, econ, false, annualNet),
  }
  const summer = {
    breakEven: breakEvenCount(s, econ, true, annualNet),
    actual: runsSummer,
    profit: quarterProfit(runsSummer, s, econ, true, annualNet),
  }
  schoolYear.offer = programsToOffer(schoolYear.breakEven, s.cancelRate)
  summer.offer = programsToOffer(summer.breakEven, s.cancelRate)

  const contributionClass = econ.class.net - econ.class.wages
  const contributionCamp = econ.camp.net - econ.camp.wages - econ.camp.shirt

  return {
    econ,
    schoolYear,
    summer,
    annual: {
      schoolYearTotal: schoolYear.profit * 3,
      summerTotal: summer.profit,
      net: schoolYear.profit * 3 + summer.profit,
      netRevenue: annualNet,
    },
    contributionClass,
    contributionCamp,
    campMultiple: contributionClass > 0 ? contributionCamp / contributionClass : null,
    fixedPerQuarter: fixedCostsPerQuarter(s),
  }
}
